/**
 * @module app.um
 */

import { autoinject, observable, _emptyParameters } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { validate } from 'app/shared/elements/ess-rule';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthReloadEvent, AuthChangeEvent } from 'app/um/lib/events';
import { apiCall } from 'app/shared/lib/api/api';
import { getReference } from 'app/shared/models/essette-types';
import { deepClone } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';


@autoinject()
export class AuthStatusPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    private authId: string | null = null;
    protected auth: UM.Auth | null = null;

    //NJ: What are these for?
    private statuses: ReadonlyArray<UM.AuthStatus> = new Array();
    private serviceDates: UM.AuthServiceDateHistory | null = null;
    private initialServiceDates: UM.AuthServiceDateHistory | null = null;
    protected authStatus: UM.AuthStatus = {};

    @observable
    protected selectedStatus: UM.AuthStatus | null = null;
    protected selectedStatusReason: UM.AuthStatusReason | null = null;
    protected reasons: ReadonlyArray<UM.AuthStatusReason> | null = null;

    protected validate = validate;

    private authReloaded: Subscription | null = null;

    protected readonly: boolean = false;
    protected hasEditClosedAuth: boolean = false;
    protected hasOverrideDatesOfService: boolean = true;
    protected allowNullDateOfService: boolean = false;

    protected saveAuthInProgress: boolean = false;
    protected hasSaveAuthErrors: boolean = false;
    protected saveAuthErrors: ReadonlyArray<UM.AuthValidationResult> = [];

    protected headerLevelTat: boolean = true;
    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly authService: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        private readonly context: AuthPageContext,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        return true;
    }

    protected async activate(params: { authId: string }): Promise<void> {
        this.auth = this.context.auth;
        this.authId = this.auth?.id ?? this.authId;

        this.checkPermissions();

        this.loadLookups();

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.auth = this.context.auth;
            this.checkPermissions();
            this.loadLookups();
            this.checkLatestTransaction(false);
        });

        this.checkLatestTransaction(false);
    }

    protected deactivate(): void {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        this.hasEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.hasOverrideDatesOfService = this.accountContext.hasPermission('UM.Auth.Duty.OverrideDOS');
        this.allowNullDateOfService = this.accountContext.hasPermission('UM.Auth.Duty.InsertNullDOS');

        this.readonly = this.context?.authIsReadOnly(canEditAuth, this.hasEditClosedAuth);
        this.headerLevelTat = !this.context.lineLevelTAT;
    }

    private async reload(): Promise<void> {
        await this.context.refreshAuthSettings();
        this.auth = this.context.auth;
        this.loadLookups();
    }

    protected filterStatuses = (status: UM.AuthStatus): boolean => {
        if(!status.determination) { return false; }
        if(status.flag?.isVoided && !status.flag?.isApproval) { return true; } //return Voided and Approval services

        //check auth validation and discharge information and return false if either fail
        const determinationStatus = !status!.flag!.isOpen || status!.flag!.isApproval || false;
        let requireDischargeMissing: boolean = false;
        if(!!this.auth?.class?.subClass?.isAdmission && !!this.auth?.type?.flag?.requiresDischarge && !!this.auth?.discharge!) {
            requireDischargeMissing = !this.auth!.discharge!.date;
        }
        if(determinationStatus && (!this.context.authRequiredForDeterminationMet || requireDischargeMissing)) {
            return false;
        }

        return true; //return final statuses once all prior validation passes
    }

    protected selectedStatusChanged(newValue: UM.AuthStatus, oldValue: UM.AuthStatus) {
        if(newValue && newValue.id && this.statuses) {
            const foundStatus = this.statuses.find(s => s.id == newValue.id);
            this.reasons = null;
            if(foundStatus && foundStatus.reasons) {
                this.reasons = foundStatus.reasons;
                this.selectedStatusReason = null;
            }
        }
        else {
            this.reasons = null;
        }
        this.selectedStatusReason = null;
    }

    private constructSaveObject(nullDates: boolean): UM.Auth {
        if(!this.auth || !this.auth.id) { return {}; }

        if(!this.selectedStatus || !this.selectedStatus.id) { return {}; }

        const saveAuth: UM.Auth = {
            id: this.auth.id,
            status: getReference(this.selectedStatus) || undefined,
            serviceDate: {
                dateRange: {
                    start: nullDates ? null : undefined,
                    end: nullDates ? null : undefined,
                }
            }
        }

        if(this.selectedStatusReason && saveAuth.status) {
            saveAuth.status.reason = getReference(this.selectedStatusReason) || undefined;
        }

        return saveAuth;
    }

    private async checkLatestTransaction(reload: boolean): Promise<void> {
        if(reload) await this.context.refreshAuthSettings();

        const latestEagleTransaction = this.context.latestEagleTransaction;
        if(this.context.syncToClaimsSystem && latestEagleTransaction.id) {
            const eagleTransactionId = latestEagleTransaction.id;
            const timestamp = latestEagleTransaction.metadata?.created?.timestamp;
            const completed = latestEagleTransaction.metadata?.completed?.timestamp;
            const validationResults = this.auth?.validation?.results;

            this.saveAuthInProgress = !completed;

            if(validationResults?.length) {
                this.hasSaveAuthErrors = true;
                await this.getAuthValidatorError();
            } else {
                this.hasSaveAuthErrors = false;
            }

            if(!timestamp || !eagleTransactionId || completed) return;

            // unlock status after a configured number of seconds (eagleAuthClaimSyncTimeout in environment-config-suite.json)
            // regardless of claims system response
            const eagleTimeout = this.env.config.eagleAuthClaimSyncTimeoutSeconds;
            let timestampDate = new Date(timestamp);
            timestampDate.setSeconds(timestampDate.getSeconds() + eagleTimeout);

            const transactionExpirationTimestamp = DateTimeValue.fromNativeDate(new Date(timestampDate));
            const now = DateTimeValue.fromNativeDate(new Date(this.context.timestamp ?? this.env.now()));

            if(now.isAfter(transactionExpirationTimestamp)) {
                // if the configured time has already elapsed...
                await this.timeoutLatestTransaction(eagleTransactionId);
            } else {
                // otherwise, set a timer for the difference...
                setTimeout(async () => { await this.checkLatestTransaction(true); }, transactionExpirationTimestamp.difference(now, 'milliseconds'));
            }
        }
        else {
            this.saveAuthInProgress = false;
            this.hasSaveAuthErrors = false;
            this.saveAuthErrors = [];
        }
    }

    private async timeoutLatestTransaction(eagleTransactionId: string) {
        const auth: UM.Auth = {
            id: this.auth?.id,
            validation: {
                isValid: false,
                generalTimeout: true,
                eagleTransaction: {
                    id: eagleTransactionId
                }
            }
        };
        const timeoutResults = await this.authService.validateAuth(auth);
        if(timeoutResults.isSuccessful) this.toaster.error().message(this.i18n.tr('um:messages.claimSyncTimeout')).show();

        this.saveAuthInProgress = false;
    }

    private reset(): void {
        this.selectedStatus = null;
        this.selectedStatusReason = null;
    }

    private async cancelServiceDates(): Promise<void> {
        this.serviceDates = deepClone(this.initialServiceDates || {});
    }

    @apiCall
    protected async saveServiceDates(): Promise<void> {
        const saveAuth: UM.Auth = {
            id: this.authId,
            serviceDate: {
                dateRange: {
                    start: this.serviceDates?.dateRange?.start,
                    end: this.serviceDates?.dateRange?.end
                }
            }
        };

        try {
            this.state.beginSave();

            const response = await this.authService.saveAuth(saveAuth);
            this.toaster.standard.saved('um:auth.serviceDate.$long', response.isSuccessful);
            if(response.isSuccessful) {
                await this.reload()

                if(saveAuth.serviceDate?.dateRange?.start != this.auth?.serviceDate?.dateRange?.start ||
                    saveAuth.serviceDate?.dateRange?.end != this.auth?.serviceDate?.dateRange?.end) {
                    this.toaster.warn().message(this.i18n.tr('um:auth.serviceDate.dateRange.systemOverride')).show();
                }

                this.events.publish(new AuthChangeEvent());
                this.context.triggerCorrespondenceRules();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const statusIncludes: string[] = [
            'reasons',
            'transitions'
        ];

        const statusFields: string[] = [
            'flag.isOpen',
            'reasons.description',
            'transitions.description',
            'transitions.determination.*',
            'transitions.flag.isApproval',
            'transitions.flag.isOpen',
            'transitions.flag.isVoided',
        ];

        const serviceDateFields: string[] = [
            'dateRange.start',
            'dateRange.end',
            'isStartDateLocked',
            'isEndDateLocked'
        ];

        let sorting: Sorting = new Sorting();
        sorting.add({
            reference: 'metadata.started.timestamp',
            direction: SortDirection.Descending
        });

        await this.doLoad(async () => {
            if(!this.authId) { return; }

            const [statuses, serviceDates] = await Promise.all([
                this.authService.loadAuthStatuses({ all: true }, statusIncludes, {}, statusFields),
                this.authService.loadServiceDateHistory(this.authId, { top: 1, sorting: sorting }, serviceDateFields)
            ]);

            this.statuses = statuses.results;
            this.serviceDates = serviceDates.results[0];
            this.initialServiceDates = deepClone(this.serviceDates || {});

            const authStatus = this.auth?.status;
            if(authStatus && authStatus.id) {
                // NJ Note: if an auth's current status is inactive, it won't be found in this lookup.  It winds up
                // leaving the start/end service dates locked, and unable to transition the auth to other statuses.
                // For now, everyone sounds fine with this, so I'm moving on.
                this.authStatus = this.statuses.find(s => s.id === authStatus.id) || {};
                this.selectedStatus = null;
            }
        });
    }

    @apiCall
    protected async saveAuthStatus(event: Event, nullDates: boolean): Promise<void> {
        const saveAuth = this.constructSaveObject(nullDates);

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.saveAuthStatus(saveAuth);
            isSuccessful = response.isSuccessful;
            this.reset();
            await this.reload();
            this.events.publish(new AuthChangeEvent(true));
            this.context.triggerCorrespondenceRules();

            if(this.context.syncToClaimsSystem) this.checkLatestTransaction(false);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:auth.status.$', isSuccessful);
            this.state.endSave();
        }

    }

    @apiCall
    protected async getAuthValidatorError(): Promise<void> {
        if(!this.authId) return;
        const response = await this.authService.loadAuthValidationResults(this.authId);
        this.saveAuthErrors = response.results;
    }
}

